<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    title="Edit additional settings"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <v-row align="center">
          <v-col class="font-weight-bold">
            Crossfade Configuration
          </v-col>
          <v-col cols="6">
            <v-btn-toggle
              v-model="manualFader"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                Default
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Manual
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-card
          v-if="manualFader"
          outlined
        >
          <v-card-text>
            <v-row
              no-gutters
              align="center"
            >
              <v-col>
                Fade-in duration
              </v-col>
              <v-col
                cols="auto"
                class="pr-3"
              >
                <strong>{{ faderSettings.fadeIn }} s</strong>
              </v-col>
              <v-col cols="8">
                <v-slider
                  v-model="faderSettings.fadeIn"
                  min="0.5"
                  max="10"
                  dense
                  hide-details
                  :step="0.1"
                >
                  <template v-slot:append>
                    <v-icon
                      class="pt-1"
                      color="primary"
                      @click="faderSettings.fadeIn += 0.1"
                    >
                      fa fa-plus-circle
                    </v-icon>
                  </template>
                  <template v-slot:prepend>
                    <v-icon
                      class="pt-1"
                      color="primary"
                      @click="faderSettings.fadeIn -= 0.1"
                    >
                      fa fa-minus-circle
                    </v-icon>
                  </template>
                </v-slider>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
            >
              <v-col>
                Fade-out duration
              </v-col>
              <v-col
                cols="auto"
                class="pr-3"
              >
                <strong>{{ faderSettings.fadeOut }} s</strong>
              </v-col>
              <v-col cols="8">
                <v-slider
                  v-model="faderSettings.fadeOut"
                  min="0.5"
                  max="10"
                  dense
                  hide-details
                  :step="0.1"
                >
                  <template v-slot:append>
                    <v-icon
                      class="pt-1"
                      color="primary"
                      @click="faderSettings.fadeOut += 0.1"
                    >
                      fa fa-plus-circle
                    </v-icon>
                  </template>
                  <template v-slot:prepend>
                    <v-icon
                      class="pt-1"
                      color="primary"
                      @click="faderSettings.fadeOut -= 0.1"
                    >
                      fa fa-minus-circle
                    </v-icon>
                  </template>
                </v-slider>
              </v-col>
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  Fade shift
                </v-col>
                <v-col
                  cols="auto"
                  class="pr-3"
                >
                  <strong>{{ faderSettings.fadeShift }} s</strong>
                </v-col>
                <v-col cols="8">
                  <v-slider
                    v-model="faderSettings.fadeShift"
                    min="-5"
                    max="5"
                    dense
                    hide-details
                    :step="0.1"
                  >
                    <template v-slot:append>
                      <v-icon
                        class="pt-1"
                        color="primary"
                        @click="faderSettings.fadeShift += 0.1"
                      >
                        fa fa-plus-circle
                      </v-icon>
                    </template>
                    <template v-slot:prepend>
                      <v-icon
                        class="pt-1"
                        color="primary"
                        @click="faderSettings.fadeShift -= 0.1"
                      >
                        fa fa-minus-circle
                      </v-icon>
                    </template>
                  </v-slider>
                </v-col>
              </v-row>
            </v-row>
          </v-card-text>
        </v-card>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import Radios from '@/services/Radios'

export default {
  name: 'EditRadioSettingsDialog',
  components: { CardDialog },
  props: {
    radio: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      radioDirty: cloneDeep(this.radio),
      manualFader: false,
      faderSettings: {
        fadeIn: 2,
        fadeOut: 2,
        fadeShift: -2
      },
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    modalVisible(visible) {
      if (!visible) return

      this.radioDirty = cloneDeep(this.radio)

      if (this.radio.fader_settings !== null) {
        this.manualFader = true

        this.faderSettings.fadeIn = this.radio.fader_settings.fade_in_duration / 1000
        this.faderSettings.fadeOut = this.radio.fader_settings.fade_out_duration / 1000
        this.faderSettings.fadeShift = this.radio.fader_settings.fade_shift_duration / 1000
      }
    }
  },
  created() {
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let data

        if (!this.manualFader) {
          data = {
            fader_settings: null
          }
        } else {
          data = {
            fader_settings: {
              fade_in_duration: Math.round(this.faderSettings.fadeIn * 1000),
              fade_out_duration: Math.round(this.faderSettings.fadeOut * 1000),
              fade_shift_duration: Math.round(this.faderSettings.fadeShift * 1000)
            }
          }
        }

        const result = await Radios.update(this.radioDirty.id, data)

        this.$emit('radio:updated', result)

        this.$store.dispatch('alert', {
          text: 'Radio playback settings successfully updated',
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting radio playback settings',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
